import React, { useRef } from 'react'
import { Icon } from '../../Icons/Icon'
import { ReactComponent as CaretIcon } from '../../../images/Icon_Caret.svg'
import classNames from 'classnames'
import { createMultilineSentence } from '../../../helper/string'
import { Button } from '../../../components/Button/Button'
import { formatPathname } from 'utils/accountUrl'

export interface NavMenuButtonProps {
  active?: boolean;
  hasDropdown?: boolean;
  badge?: React.ReactNode;
  /**
   * Text to display on the button
   */
  label: string;
  href?: string;
  hasAccordionCaret?: boolean;
  innerRef?: React.RefObject<any>;
  locationUrls?: Array<String>;
  className?: string
  /**
   * Click handler
   */
  onClick?: ((event: any) => void) | (() => Promise<any>)
  icon?: string
}

export const NavMenuButton: React.FC<NavMenuButtonProps> = ({
  active,
  hasDropdown = false,
  badge,
  label,
  href,
  hasAccordionCaret,
  innerRef,
  onClick,
  icon,
  locationUrls,
  className,
  ...props
}) => {
  const ref = useRef<HTMLAnchorElement | HTMLButtonElement>(null)

  // active checks are for the 3 navigation menus for the underline
  // pathname and isPathInLocationUrls is logic specifically for the account dropdown (username)
  const pathname = formatPathname(window.location.pathname)
  const isPathInLocationUrls = locationUrls?.includes(pathname)

  const multilineElements = createMultilineElements(label, [
    { maxChars: 12, className: 'nav-text nav-text--1' },
    { maxChars: 16, className: 'nav-text nav-text--2' },
    { maxChars: 32, className: 'nav-text nav-text--3' }
  ])

  const iconLabelClass = classNames(`
    nav-menu-button-content-container ds-u-display--flex 
    ds-u-align-items--center ds-u-font-size--base ds-u-valign--bottom`,
  { 'nav-menu-button-content-container--active': active || isPathInLocationUrls })

  const iconAndLabel = () => {
    return (
      <>
        <span className={iconLabelClass}>
          {icon ? <Icon img={icon} text={label} /> : multilineElements}
        </span>
        {hasAccordionCaret && <CaretIcon className='ch-c-caret-fixed-size ch-u-margin-left--4px' aria-hidden='true' focusable='false' />}
      </>
    )
  }

  const buttonClassName = classNames(
    'nav-menu-button ds-u-font-size--base ds-u-sans',
    (href ? 'ds-u-display--flex' : 'ds-u-valign--bottom'),
    className
  )

  return (
    <Button
      ref={ref}
      className={buttonClassName}
      href={href}
      onClick={async (event: {preventDefault: () => void}) => {
        event.preventDefault()

        if (ref.current) {
          ref.current.focus()
        }

        if (onClick) {
          await onClick(event)
        }
        if (href) {
          window.location.href = href
        }
      }}
      aria-expanded={hasDropdown ? (active || false) : undefined}
    >
      {iconAndLabel()}
      {badge || ''}
    </Button>
  )
}

// TODO - for more dynamic support, pass arrayOfMaxCharsWithClassList
// from "NavMenu" component by first measuring all NavMenuText label lengths,
// should "NavMenu" component have a props flag for requesting this calculation
// ...
// then calculating the total spacing width available to component
// pass in there a parameter "EN" or "ES" as well
// use a dictionary to reference a lookup for media queries needed and linebreak spaces
// you can also generate an inline <style> tag on element for javascript-generated-css-media-queries
function createMultilineElements (
  sentence: string,
  arrayOfMaxCharsWithClassList: Array<{ maxChars: number; className: string }>
): React.ReactNode[] {
  return arrayOfMaxCharsWithClassList.map((item, index) => {
    const labelText = createMultilineSentence(sentence, item.maxChars)
    if (index === 0) {
      return (
        <span className={item.className} key={index} aria-label={labelText}>
          {labelText}
        </span>
      )
    } else {
      return (
        <span className={item.className} key={index}>
          {labelText}
        </span>
      )
    }
  })
};
